import { Component, input, output, signal, effect, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ZagIcon } from '../../icon/icon';

@Component({
  selector: 'zag-search',
  imports: [FormsModule, TranslateModule, ZagIcon],
  templateUrl: './card-search.component.html',
  styleUrls: ['./card-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardSearchComponent implements OnDestroy {
  /**
   * Placeholder text for search input (translation key)
   */
  placeholder = input<string>('Search...');

  /**
   * Debounce time in milliseconds
   */
  debounceMs = input<number>(300);

  /**
   * Minimum characters before emitting search term
   */
  minChars = input<number>(1);

  /**
   * Current search term (input)
   */
  searchTerm = input<string>('');

  /**
   * Search term change event (output)
   */
  searchTermChange = output<string>();

  /**
   * Internal immediate search value
   */
  immediateSearchValue = signal<string>('');

  /**
   * Debounce timer
   */
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Update model with debounced immediate value
    effect(() => {
      const term = this.immediateSearchValue();
      this.scheduleEmit(term);
    });
  }

  /**
   * Handle input change
   */
  onInputChange(value: string): void {
    this.immediateSearchValue.set(value);
  }

  /**
   * Clear search term
   */
  clearSearch(): void {
    this.immediateSearchValue.set('');
    this.searchTermChange.emit('');
  }

  /**
   * Schedule update of search term with debounce
   */
  private scheduleEmit(term: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.searchTermChange.emit(term);
    }, this.debounceMs());
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
